import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber, toString } from 'lodash';

import { api } from '@/api';
import { PATHS } from '@/common';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL, IDependentResponseDto } from '@/dtos';
import {
  CREATION_SOURCE_TYPE,
  CreationSourceType,
  Dependent,
  DependentPayload,
  DependentStatusEnum,
} from '@/modules/employee/employee.types';

import { formatDependentSsnToServer, getMaskedDependentSsn } from '../dependents.utils';

export const GET_DEPENDENTS_BY_EMPLOYEE_ID_KEY = 'GET_DEPENDENTS_BY_EMPLOYEE_ID_KEY';

export const useGetDependentsQuery = (employeeId?: string, selectedStatuses?: DependentStatusEnum[]) => {
  const { isLoading, isSuccess, isError, data } = useQuery(
    [GET_DEPENDENTS_BY_EMPLOYEE_ID_KEY, employeeId, selectedStatuses],
    () =>
      api.get<IDependentResponseDto[]>(API_URL.V2_DEPENDENTS_EMPLOYEE_BY_EMPLOYEE_ID(employeeId || ''), {
        headers: {
          'X-DETAILS-RESOLVERS': 'DEPENDENT',
        },
        params: {
          ...(selectedStatuses
            ? {
                statuses: selectedStatuses,
              }
            : {}),
        },
      }),
    {
      enabled: Boolean(employeeId),
    }
  );

  const dependents = useMemo<Dependent[]>(
    () =>
      data?.data?.map((dependent) => {
        const item: Dependent = {
          dependentId: toString(dependent?.dependent_id),
          employeeId: toString(dependent?.employee_id),
          firstName: dependent?.first_name,
          middleName: dependent?.middle_name,
          lastName: dependent?.last_name,
          fullName: `${dependent?.first_name || ''} ${dependent?.last_name || ''}`.trim(),
          ssn: getMaskedDependentSsn(dependent?.ssn),
          genderType: dependent?.gender_type,
          birthOn: dependent?.birth_on as unknown as string,
          relationshipType: dependent?.relationship_type,
          creationSourceType: dependent?.creation_source_type as unknown as CreationSourceType,
          status: dependent?.status as unknown as DependentStatusEnum,
        };
        return item;
      }) || [],
    [data]
  );

  return {
    dependents,
    isError,
    isLoading,
    isSuccess,
  };
};

const formatDependentToServer = (dependent?: Dependent, employeeId?: string) => ({
  first_name: dependent?.firstName,
  last_name: dependent?.lastName,
  ...formatDependentSsnToServer(dependent?.ssn),
  ...(dependent?.birthOn ? { birth_on: dayjs(dependent.birthOn).format(RAW_DATE_FORMAT) } : {}),
  employee_id: toNumber(dependent?.employeeId || employeeId),
  creation_source_type: CREATION_SOURCE_TYPE,
  relationship_type: dependent?.relationshipType,
});

const CREATE_DEPENDENT_KEY = 'update_card_holder';

interface Props {
  employeeId?: string;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onSuccess?: (response?: any) => void;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onError?: (response?: any) => void;
  hasCardHolder?: boolean;
}
export const useCreateDependentQuery = ({ employeeId, onSuccess, onError, hasCardHolder }: Props) => {
  const query = useMemo(() => {
    const queryGenerator = new URLSearchParams();

    queryGenerator.set(CREATE_DEPENDENT_KEY, `${Boolean(hasCardHolder)}`);

    return queryGenerator;
  }, [hasCardHolder]);
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation(
    (value: Dependent) =>
      api.post(`${API_URL.V2_DEPENDENTS}?${query}`, formatDependentToServer(value, employeeId)),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: [GET_DEPENDENTS_BY_EMPLOYEE_ID_KEY] });
        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: (response) => {
        if (onError) {
          onError(response);
        }
      },
    }
  );

  const createDependent = useCallback(
    async (value: Dependent) => {
      const response = await mutateAsync(value);
      return response?.data as DependentPayload;
    },
    [mutateAsync]
  );

  return {
    isLoading,
    createDependent,
  };
};

export const useUpdateDependent = (props?: Props) => {
  const queryClient = useQueryClient();
  const query = useMemo(() => {
    const queryGenerator = new URLSearchParams();

    queryGenerator.set(CREATE_DEPENDENT_KEY, `${Boolean(props?.hasCardHolder)}`);

    return queryGenerator;
  }, [props]);

  const { mutateAsync, isLoading, isError } = useMutation(
    (dependent: Dependent) =>
      api.put(
        `${API_URL.V2_DEPENDENTS_BY_DEPENDENT_ID(dependent?.dependentId || '')}?${query}`,
        formatDependentToServer(dependent)
      ),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: [GET_DEPENDENTS_BY_EMPLOYEE_ID_KEY] });
        if (props?.onSuccess) {
          props.onSuccess(response);
        }
      },
      onError: (response) => {
        if (props?.onError) {
          props.onError(response);
        }
      },
    }
  );

  return {
    updateDependent: mutateAsync,
    isLoading,
    isError,
  };
};

export const useUpdateCardDependent = () => {
  const { mutateAsync, isLoading, isError } = useMutation((data: Dependent) =>
    // TODO: this url is missing
    api.post(PATHS.ADD_CARD_FOR_DEPENDENT(data.dependentId), formatDependentToServer(data))
  );

  return {
    updateDependentCard: mutateAsync,
    isLoading,
    isError,
  };
};
